<script setup>
/**
 * One message in the log.
 *
 * Same row grammar as `BroadcastRow` and `TaskCard` — **[what · when · why] [state] [go]**
 * — so the eye finds the control in the same place whatever the row says and whichever
 * direction its text runs.
 *
 * EVERY FACT ON THIS ROW IS SOMEBODY ELSE'S STRING. The recipient is whatever number the
 * owner's own system asked for, the message name is whatever WhatsApp approved, and the
 * failure line is whatever upstream said — none of them are lorem ipsum and none of them
 * wrap on their own (README §2). So each is isolated with `<bdi>` (a `+965…` number sitting
 * in an Arabic row otherwise swaps its `+` to the far side), wraps at any point, and the
 * flex column that holds them carries `min-w-0` so it can actually shrink instead of
 * pushing the chip and the link off the end of a 420px panel (README §1).
 *
 * THE LABELS ARE THERE AND THEY ARE INVISIBLE. Three stacked lines of bare values read
 * fine with eyes and read as a shopping list without them, so `ROW.TO`/`TEMPLATE`/`WHEN`
 * are `sr-only` — the fact each line states, for anyone who cannot see that the first one
 * is a phone number.
 *
 * WHEN = WHEN THE OWNER'S SYSTEM ASKED FOR IT, not the sort key. The log is ordered by
 * last change (`updated_at DESC`), because a log of things that keep changing is read
 * newest-event-first; but the instant an owner matches against her OWN system's record is
 * the one she asked at, so that is the primary date and it stays primary.
 *
 * AND THE ROW SAYS WHY IT MOVED. Those two instants are days apart for a message whose
 * status changed long after it was sent — a late delivery receipt — so the row jumps to the
 * top of a list dated by something that did not change, and the whole list reads as
 * unsorted with nothing on screen to explain it. The second line is the explanation, and it
 * appears only when it IS one: if both instants render to the same words ("3 days ago"),
 * the sentence would be noise, so it is dropped. Comparing the RENDERED strings rather than
 * a millisecond threshold is deliberate — the question is whether the reader can see a
 * difference, and the renderer is the only thing that knows.
 *
 * The detail page still shows all five instants exactly; this is the one-line version.
 *
 * The clock is the SERVER's (`serverTime`), never this browser's: a laptop whose clock is
 * a day out would otherwise render every row as "yesterday" and nothing on screen would
 * say why.
 */
import { computed } from 'vue';

import { frontendURL } from 'dashboard/helper/URLHelper';

import OutboundMessageReason from './OutboundMessageReason.vue';
import OutboundStatusChip from './OutboundStatusChip.vue';
import { useWasloI18n } from '../../i18n.js';
import { relativeTime } from '../../helpers/relative-time.js';

const props = defineProps({
  /** A row from `wasloApi.getOutboundMessages`. */
  message: { type: Object, required: true },
  accountId: { type: Number, default: null },
  /** The server's clock, from the same response these rows came in. */
  serverTime: { type: String, default: '' },
});

const { t, locale } = useWasloI18n();

const now = computed(() => {
  const parsed = Date.parse(props.serverTime);
  return Number.isNaN(parsed) ? undefined : parsed;
});

const since = iso =>
  relativeTime(iso, locale.value, {
    now: now.value,
    justNow: t('WASLO.OUTBOUND.LOG.ROW.JUST_NOW'),
  }) ?? '';

const when = computed(() => since(props.message.createdAt));

/**
 * The sort key, in words — and empty whenever showing it would tell the reader nothing.
 *
 * Three ways it is nothing: the two instants are the same string (the ordinary case, where
 * a message finished a second after it was asked for), `updatedAt` did not parse, or the
 * created line is not on screen for it to sit under.
 */
const changed = computed(() => {
  const updated = since(props.message.updatedAt);
  if (updated === '' || updated === when.value) return '';
  return t('WASLO.OUTBOUND.LOG.ROW.UPDATED', { when: updated });
});

const to = computed(() =>
  props.accountId === null
    ? null
    : frontendURL(
        `accounts/${props.accountId}/waslo/outbound/log/${encodeURIComponent(
          props.message.id
        )}`
      )
);
</script>

<template>
  <li
    class="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-start"
    :data-waslo-outbound-message="message.id"
  >
    <div class="flex flex-col min-w-0 gap-1 sm:flex-1">
      <p
        class="mb-0 text-sm font-medium break-words [overflow-wrap:anywhere] text-n-slate-12"
        data-waslo-outbound-to
      >
        <span class="sr-only">{{ t('WASLO.OUTBOUND.LOG.ROW.TO') }}</span>
        <bdi>{{ message.to }}</bdi>
      </p>
      <p
        class="mb-0 text-sm break-words [overflow-wrap:anywhere] text-n-slate-11"
        data-waslo-outbound-template
      >
        <span class="sr-only">{{ t('WASLO.OUTBOUND.LOG.ROW.TEMPLATE') }}</span>
        <bdi>{{ message.template }}</bdi>
      </p>
      <p v-if="when" class="mb-0 text-xs text-n-slate-10">
        <span class="sr-only">{{ t('WASLO.OUTBOUND.LOG.ROW.WHEN') }}</span>
        {{ when }}
      </p>
      <!-- Why this row is where it is. Only ever rendered when it says something the
           line above does not. -->
      <p
        v-if="when && changed"
        class="mb-0 text-xs text-n-slate-10"
        data-waslo-outbound-changed
      >
        {{ changed }}
      </p>
      <OutboundMessageReason :message="message" clamp />
    </div>

    <div class="flex items-center gap-3 shrink-0 ms-0 sm:ms-auto">
      <OutboundStatusChip :status="message.status" />
      <router-link
        v-if="to"
        :to="to"
        class="inline-flex items-center h-8 gap-1 px-3 text-sm font-medium rounded-lg shrink-0 text-n-slate-12 bg-n-alpha-2 hover:bg-n-slate-4"
        data-waslo-outbound-open
      >
        {{ t('WASLO.OUTBOUND.LOG.ROW.OPEN') }}
      </router-link>
    </div>
  </li>
</template>
